import React from 'react';
import './HighlightText.scss';

export interface HighlightTextProps {
  /** The text to display */
  text: string;
  /** Search query to highlight (supports multiple space-separated terms) */
  query?: string;
  /** Custom class name for the container */
  className?: string;
}

/**
 * Escape special regex characters in a string
 */
const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * HighlightText - Renders text with search query matches highlighted.
 * Supports multiple search terms (space-separated), case-insensitive.
 */
export const HighlightText = ({ text, query, className }: HighlightTextProps) => {
  // No query or empty text = return plain text
  if (!query?.trim() || !text) {
    return <span className={className}>{text}</span>;
  }

  // Split query into terms and escape each for regex
  const terms = query.trim().split(/\s+/).filter(Boolean).map(escapeRegex);

  if (terms.length === 0) {
    return <span className={className}>{text}</span>;
  }

  // Create regex that matches any of the terms
  const regex = new RegExp(`(${terms.join('|')})`, 'gi');

  // Split text by matches
  const parts = text.split(regex);

  return (
    <span className={className}>
      {parts.map((part, index) => {
        // Check if this part matches any term
        const isMatch = terms.some(term => new RegExp(`^${term}$`, 'i').test(part));
        return isMatch ? (
          <mark key={index} className="ksq2-highlight">
            {part}
          </mark>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        );
      })}
    </span>
  );
};

export default HighlightText;
